import { Injectable, OnDestroy, inject } from '@angular/core';
import { Subscription, firstValueFrom } from 'rxjs';
import { WalletManager } from '../../managers/wallet.manager';
import {
  Kcc20Referral,
  Kcc20ReferralApiService,
} from './kcc20-referral-api.service';

export const KCC20_PENDING_REFERRAL_CODE_KEY = 'kcc20-referral-code';
export const KCC20_REFERRAL_CODE_RE = /^[a-z0-9]{3,32}$/;

export function normalizeKcc20ReferralCode(
  code?: string | null,
): string | undefined {
  const normalized = code?.trim().toLowerCase();
  if (!normalized || !KCC20_REFERRAL_CODE_RE.test(normalized)) {
    return undefined;
  }
  return normalized;
}

@Injectable({ providedIn: 'root' })
export class Kcc20ReferralAttributionService implements OnDestroy {
  private readonly walletManager = inject(WalletManager);
  private readonly referralApi = inject(Kcc20ReferralApiService);
  private walletChanges?: Subscription;
  private applying?: Promise<Kcc20Referral | undefined>;
  private inMemoryPendingCode?: string;
  private started = false;

  start(): void {
    if (this.started) return;
    this.started = true;
    this.captureFromCurrentUrl();
    this.walletChanges = this.walletManager.onWalletChangedNotifier.subscribe(
      () => {
        void this.applyPendingReferral().catch(() => {});
      },
    );
    void this.applyPendingReferral().catch(() => {});
  }

  ngOnDestroy(): void {
    this.walletChanges?.unsubscribe();
  }

  captureFromCurrentUrl(): string | undefined {
    const url = this.currentUrl();
    const code = normalizeKcc20ReferralCode(
      url?.searchParams.get('ref') ?? undefined,
    );
    if (!url?.searchParams.has('ref')) return undefined;

    if (code) {
      this.writePendingCode(code);
    }

    url.searchParams.delete('ref');
    const next = `${url.pathname}${url.search}${url.hash}`;
    this.replaceCurrentUrl(next || '/');
    return code;
  }

  pendingCode(): string | undefined {
    return normalizeKcc20ReferralCode(this.readPendingCode());
  }

  clearPendingCode(code?: string): void {
    const pending = this.pendingCode();
    if (!code || !pending || pending === normalizeKcc20ReferralCode(code)) {
      this.removePendingCode();
    }
  }

  applyPendingReferral(): Promise<Kcc20Referral | undefined> {
    const pending = this.pendingCode();
    const owner = this.walletManager.signedWalletSignal()?.kcc20Owner;
    if (!pending || !owner) return Promise.resolve(undefined);
    if (this.applying) return this.applying;

    this.applying = this.applyPendingReferralNow(pending).finally(() => {
      this.applying = undefined;
    });
    return this.applying;
  }

  private async applyPendingReferralNow(
    pending: string,
  ): Promise<Kcc20Referral | undefined> {
    const referral = await firstValueFrom(
      this.referralApi.upsertMyReferral(
        { referredByCode: pending },
        'referral-attribution',
      ),
    );

    if (referral.referredByCode) {
      this.clearPendingCode(pending);
    } else if (referral.code === pending) {
      this.clearPendingCode(pending);
    } else if (referral.canAddReferredBy) {
      this.clearPendingCode(pending);
    }

    return referral;
  }

  private readPendingCode(): string | undefined {
    if (this.inMemoryPendingCode) {
      return this.inMemoryPendingCode;
    }
    try {
      return (
        window.localStorage.getItem(KCC20_PENDING_REFERRAL_CODE_KEY) ??
        undefined
      );
    } catch {
      return undefined;
    }
  }

  private writePendingCode(code: string): void {
    this.inMemoryPendingCode = code;
    try {
      window.localStorage.setItem(KCC20_PENDING_REFERRAL_CODE_KEY, code);
    } catch {
      return;
    }
  }

  private removePendingCode(): void {
    this.inMemoryPendingCode = undefined;
    try {
      window.localStorage.removeItem(KCC20_PENDING_REFERRAL_CODE_KEY);
    } catch {
      return;
    }
  }

  private replaceCurrentUrl(url: string): void {
    try {
      window.history.replaceState(window.history.state, '', url);
    } catch {
      return;
    }
  }

  private currentUrl(): URL | undefined {
    try {
      return new URL(window.location.href);
    } catch {
      return undefined;
    }
  }
}
